package top.wcpe.allincore.dsl.compiler.semantic

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.LValueAst
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.ast.TypeAst
import top.wcpe.allincore.dsl.compiler.ast.displayName
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.type.IrType
import top.wcpe.allincore.dsl.compiler.type.TypeInferencer

/**
 * M10 Stage 1：拆分自原 TypeConstraintChecker。
 *
 * 维度：state 字段赋值相关诊断。
 * 涉及诊断码：E624 / E661。
 *
 * 对应原文件方法：
 * - checkNullAssignment
 * - checkStateFieldReassign
 */
internal class StateFieldChecker(
    private val diag: DiagnosticCollector,
    private val env: VarTypeEnv,
) {

    /**
     * 检查赋值语句：若 rhs 为 null 字面量且 lhs 目标 state 字段类型非空，报 E624。
     *
     * 仅对 StateLValue + 显式类型标注的字段生效；未标注类型的字段不触发检查（兼容旧资源）。
     */
    fun onAssignStateNull(assign: StmtAst.AssignStmt) {
        if (assign.rhs !is ExprAst.NullLit) return
        val lhs = assign.lhs as? LValueAst.StateLValue ?: return
        val typeAst = env.stateFieldTypes[lhs.name] ?: return
        val nullable = when (typeAst) {
            is TypeAst.Named -> typeAst.nullable
            is TypeAst.Generic -> typeAst.nullable
            is TypeAst.Function -> typeAst.nullable
        }
        if (!nullable) {
            diag.error(
                DslError.E624_NULL_ASSIGN_TO_NON_NULLABLE,
                assign.position,
                typeAst.displayName(),
                lhs.name,
            )
        }
    }

    /**
     * M9.5 Stage 4 / E661：state 字段二次赋值类型不匹配时报 INFO。
     *
     * 命中规则：
     * - lhs 为 [LValueAst.StateLValue] 且 stateFieldIrTypes 中已登记字段类型
     * - rhs 推断类型与登记类型完全不容（双向 isAssignableTo 都失败）
     *
     * AnyT 与 Nullable(AnyT) 一律放行——保持与 E662 / E660 的一致策略，避免对绝大多数
     * 动态值产生假阳性。null 字面量赋给非空 state 字段已由 [onAssignStateNull] 走 E624
     * 错误级路径，本检查不再重复。
     */
    fun onAssignStateValue(assign: StmtAst.AssignStmt) {
        val lhs = assign.lhs as? LValueAst.StateLValue ?: return
        val declared = env.stateFieldIrTypes[lhs.name] ?: return
        // null 字面量赋值已由 onAssignStateNull 处理（兼容字段一致走 E624 错误，可空字段放行）
        if (assign.rhs is ExprAst.NullLit) return
        val actual = TypeInferencer.inferExpr(assign.rhs, env.currentVarEnv())
        if (actual.isDynamic()) return
        // 双向兼容才算"类型一致"；与 E662 边界一致
        if (actual.isAssignableTo(declared) || declared.isAssignableTo(actual)) return
        diag.info(
            DslError.E661_STATE_FIELD_REASSIGN_TYPE_MISMATCH,
            assign.rhs.position,
            lhs.name,
            declared.displayName(),
            actual.displayName(),
        )
    }
}
